import React, { useCallback, useEffect, useState } from 'react';
import { HiX } from 'react-icons/hi';
import apiClient from '../api/client';
import ModalOverlay from './ModalOverlay';

const defaultForm = {
  automation_enabled: true,
  auto_start_stop: true,
  budget_scope: 'running_and_paused',
  auto_distribute: true,
  auto_deposit: true,
};

function normalizeFormFromBudget(budget) {
  if (!budget) return { ...defaultForm };
  return {
    automation_enabled: budget.automation_enabled !== false,
    auto_start_stop: budget.auto_start_stop !== false,
    budget_scope: budget.budget_scope === 'running_only' ? 'running_only' : 'running_and_paused',
    auto_distribute: budget.auto_distribute !== false,
    auto_deposit: budget.auto_deposit !== false,
  };
}

function ToggleRow({ label, description, checked, onChange, disabled }) {
  return (
    <div
      className={`flex items-center justify-between gap-4 py-3.5 ${
        disabled ? 'opacity-50 pointer-events-none' : ''
      }`}
    >
      <div className="min-w-0 flex-1 pr-2">
        <p className="text-sm font-medium text-gray-800">{label}</p>
        {description ? (
          <p className="text-xs text-gray-500 mt-0.5 leading-snug">{description}</p>
        ) : null}
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <span className={`text-xs font-medium tabular-nums ${checked ? 'text-gray-400' : 'text-gray-700'}`}>
          Выкл
        </span>
        <label className="relative inline-flex items-center cursor-pointer">
          <input
            type="checkbox"
            checked={checked}
            onChange={(e) => onChange(e.target.checked)}
            disabled={disabled}
            className="sr-only peer"
          />
          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-200 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600" />
        </label>
        <span className={`text-xs font-medium tabular-nums ${checked ? 'text-indigo-700' : 'text-gray-400'}`}>
          Вкл
        </span>
      </div>
    </div>
  );
}

export default function AutomationControlModal({ isOpen, onClose, clientId, month, onSaved }) {
  const [form, setForm] = useState(defaultForm);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const loadSettings = useCallback(async () => {
    if (!clientId) return;
    setLoading(true);
    setError('');
    try {
      const { data } = await apiClient.get(`/budget/${clientId}`, {
        params: { month },
      });
      setForm(normalizeFormFromBudget(data?.budget));
    } catch (err) {
      setError(err?.response?.data?.message || 'Не удалось загрузить настройки');
      setForm({ ...defaultForm });
    } finally {
      setLoading(false);
    }
  }, [clientId, month]);

  useEffect(() => {
    if (isOpen && clientId) {
      loadSettings();
    }
  }, [isOpen, clientId, loadSettings]);

  const handleSave = async () => {
    if (!clientId || !month) return;
    setSaving(true);
    setError('');
    try {
      await apiClient.put(`/budget/${clientId}/settings`, {
        month,
        automation_enabled: form.automation_enabled,
        auto_start_stop: form.auto_start_stop,
        budget_scope: form.budget_scope,
        auto_distribute: form.auto_distribute,
        auto_deposit: form.auto_deposit,
      });
      if (onSaved) onSaved();
      onClose();
    } catch (err) {
      setError(err?.response?.data?.message || 'Не удалось сохранить настройки');
    } finally {
      setSaving(false);
    }
  };

  const setMasterOff = () => {
    setForm((prev) => ({
      ...prev,
      automation_enabled: false,
      auto_distribute: false,
      auto_deposit: false,
      auto_start_stop: false,
    }));
  };

  const automationOn = form.automation_enabled;

  if (!isOpen) return null;

  return (
    <ModalOverlay onClick={onClose}>
      <div
        className="modal-panel rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className="modal-header flex justify-between items-center px-5 py-4 rounded-t-2xl border-b border-white/40">
          <div>
            <h3 className="text-base font-semibold text-gray-800">Управление автоматизацией</h3>
            <p className="text-xs text-gray-500 mt-0.5">
              Ручной режим: отключите автозапуск и распределяйте бюджет только по выбранным кампаниям.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <HiX className="w-6 h-6" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {error && (
            <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
              {error}
            </div>
          )}

          {loading ? (
            <p className="text-sm text-gray-500">Загрузка…</p>
          ) : (
            <>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={setMasterOff}
                  className="text-xs font-medium px-3 py-1.5 rounded-lg border border-red-300 text-red-700 bg-red-50 hover:bg-red-100"
                >
                  Отключить всю автоматизацию
                </button>
                <button
                  type="button"
                  onClick={() => setForm({ ...defaultForm })}
                  className="text-xs font-medium px-3 py-1.5 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50"
                >
                  Включить всё по умолчанию
                </button>
              </div>

              <div className="rounded-xl border border-gray-200 bg-white/60 divide-y divide-gray-100 px-4">
                <ToggleRow
                  label="Автоматизация"
                  description="Общий выключатель автоматизации и фоновых задач"
                  checked={form.automation_enabled}
                  onChange={(on) => setForm((prev) => ({ ...prev, automation_enabled: on }))}
                />
                <ToggleRow
                  label="Автозапуск и остановка кампаний"
                  description="Алгоритм запускает и ставит кампании на паузу"
                  checked={form.auto_start_stop}
                  onChange={(on) => setForm((prev) => ({ ...prev, auto_start_stop: on }))}
                  disabled={!automationOn}
                />
                <ToggleRow
                  label="Учитывать кампании на паузе"
                  description="Выключено — бюджет только для запущенных кампаний"
                  checked={form.budget_scope === 'running_and_paused'}
                  onChange={(on) =>
                    setForm((prev) => ({
                      ...prev,
                      budget_scope: on ? 'running_and_paused' : 'running_only',
                    }))
                  }
                  disabled={!automationOn}
                />
                <ToggleRow
                  label="Автоматическое распределение бюджета"
                  description="По расписанию пересчитывает дневные лимиты"
                  checked={form.auto_distribute}
                  onChange={(on) => setForm((prev) => ({ ...prev, auto_distribute: on }))}
                  disabled={!automationOn}
                />
                <ToggleRow
                  label="Автопополнение счетов кампаний"
                  description="Пополняет бюджет кампаний при нехватке средств"
                  checked={form.auto_deposit}
                  onChange={(on) => setForm((prev) => ({ ...prev, auto_deposit: on }))}
                  disabled={!automationOn}
                />
              </div>
            </>
          )}
        </div>

        <div className="flex gap-2 px-5 pb-5">
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || loading || !month}
            className="glass-btn flex-1 py-2 disabled:opacity-50"
          >
            {saving ? 'Сохранение…' : 'Сохранить'}
          </button>
          <button type="button" onClick={onClose} className="glass-btn-secondary px-4 py-2">
            Отмена
          </button>
        </div>
      </div>
    </ModalOverlay>
  );
}
